// verified = specifically listed for the clinic.
// The rest are represented as speciality/service areas, phrased carefully.
export const treatments = [
  {
    slug: "root-canal-treatment",
    name: "Root Canal Treatment",
    short: "Endodontic",
    category: "Restorative",
    verified: true,
    icon: "root-canal",
    description:
      "Root canal treatment (RCT) is aimed at treating the innermost part of a tooth to help relieve pain and support keeping your natural tooth.",
    heroDescription:
      "A dedicated speciality area of the clinic, focused on treating the inner pulp of a tooth so the natural tooth can potentially be preserved.",
    whatIsIt:
      "Root canal treatment is a dental procedure that addresses the soft tissue inside a tooth. It is typically considered when this inner tissue becomes affected, with the aim of relieving discomfort and helping retain the natural tooth structure.",
    whenRecommended:
      "This treatment may be recommended after a dental evaluation, when a tooth's inner tissue is affected. A dentist can assess your specific situation and discuss whether this approach may be suitable for you.",
    whatToExpect:
      "Your visit typically begins with a consultation and evaluation. The dentist will explain the recommended approach and answer any questions you may have before proceeding.",
    process: [
      { step: "01", title: "Consultation", detail: "A thorough examination and discussion of your concerns and treatment options." },
      { step: "02", title: "Treatment", detail: "The recommended procedure is carried out with attention to your comfort." },
      { step: "03", title: "Restoration", detail: "The tooth is restored to help support its function going forward." },
    ],
    aftercare:
      "Your dentist will provide guidance specific to your treatment. Following their instructions and attending any recommended follow-up visits supports a smooth recovery.",
    faqs: [
      { q: "Is root canal treatment painful?", a: "Every case is different. Your dentist will discuss comfort measures and what to expect during your consultation." },
      { q: "How many visits does it usually take?", a: "This depends on the individual case. Your dentist can outline a plan after evaluating your tooth." },
    ],
  },
  {
    slug: "oral-maxillofacial-surgery",
    name: "Oral & Maxillofacial Surgery",
    short: "Surgical",
    category: "Surgical",
    verified: true,
    icon: "surgery",
    description:
      "Specialised oral and facial surgical dental procedures, handled with a focus on patient comfort and clear communication.",
    heroDescription:
      "A specialised surgical service area covering procedures involving the mouth, jaw, and related facial structures.",
    whatIsIt:
      "Oral and maxillofacial surgery covers a range of surgical procedures relating to the mouth, jaws, and face. The specific approach depends on each patient's individual situation.",
    whenRecommended:
      "A dentist may recommend a surgical consultation after evaluating your oral health and discussing your concerns in detail.",
    whatToExpect:
      "You can expect a careful evaluation, a clear explanation of the recommended procedure, and guidance on preparing for your visit.",
    process: [
      { step: "01", title: "Consultation", detail: "Assessment of your condition and discussion of the recommended surgical approach." },
      { step: "02", title: "Treatment", detail: "The procedure is carried out with an emphasis on patient comfort." },
      { step: "03", title: "Restoration", detail: "Guidance is provided on recovery and any follow-up care." },
    ],
    aftercare:
      "Post-procedure guidance is provided by your dentist. Following their instructions closely supports a comfortable recovery.",
    faqs: [
      { q: "Will I need to be referred for this?", a: "Your dentist will let you know whether an in-house consultation or referral is appropriate for your case." },
      { q: "What should I expect during recovery?", a: "Recovery varies by procedure. Your dentist will walk you through what to expect for your specific case." },
    ],
  },
  {
    slug: "fixed-prosthodontics",
    name: "Fixed Prosthodontics",
    short: "Restorative",
    category: "Restorative",
    verified: true,
    icon: "prosthodontics",
    description:
      "Restoration of missing or damaged teeth using fixed dental prostheses, planned around your bite and comfort.",
    heroDescription:
      "Fixed prosthodontic care focuses on restoring the function and appearance of missing or damaged teeth using fixed prostheses.",
    whatIsIt:
      "Fixed prosthodontics covers dental restorations such as crowns, bridges, and related fixed prostheses used to restore damaged or missing teeth.",
    whenRecommended:
      "This may be recommended when a tooth is significantly damaged, weakened, or missing, and a fixed, durable restoration could be beneficial.",
    whatToExpect:
      "Expect an evaluation of your bite and remaining tooth structure, followed by a plan tailored to your needs and a review of the restoration options.",
    process: [
      { step: "01", title: "Consultation", detail: "Evaluation of the tooth or teeth requiring restoration." },
      { step: "02", title: "Treatment", detail: "Preparation and fitting of the recommended fixed prosthesis." },
      { step: "03", title: "Restoration", detail: "Final fitting and review to help ensure comfort and fit." },
    ],
    aftercare:
      "Maintaining good oral hygiene and attending review visits helps support the longevity of a fixed prosthesis.",
    faqs: [
      { q: "How long does a fixed prosthesis last?", a: "Longevity depends on the type of prosthesis and how well it is cared for. Your dentist can advise on your specific case." },
      { q: "Will it match my natural teeth?", a: "Your dentist will discuss material and appearance options during your consultation." },
    ],
  },
  {
    slug: "cosmetic-dentistry",
    name: "Cosmetic Dentistry",
    short: "Cosmetic",
    category: "Cosmetic",
    verified: false,
    icon: "cosmetic",
    description: "A speciality area focused on the appearance of your smile alongside its health and function.",
    heroDescription: "Cosmetic dental care that considers the appearance of your smile alongside its health and function.",
    whatIsIt: "Cosmetic dentistry covers a range of approaches aimed at improving the appearance of teeth and smile.",
    whenRecommended: "A consultation helps determine which cosmetic approaches, if any, may suit your goals.",
    whatToExpect: "A discussion of your smile goals followed by a personalised recommendation from your dentist.",
    process: [
      { step: "01", title: "Consultation", detail: "Discussing your smile goals and expectations." },
      { step: "02", title: "Treatment", detail: "The agreed approach is carried out step by step." },
      { step: "03", title: "Restoration", detail: "Final review to make sure you're comfortable with the result." },
    ],
    aftercare: "Your dentist will share care guidance specific to the approach used.",
    faqs: [{ q: "Is cosmetic treatment right for me?", a: "This depends on your goals and oral health — best discussed during a consultation." }],
  },
  {
    slug: "orthodontic-care",
    name: "Orthodontic Care",
    short: "Orthodontic",
    category: "Orthodontic",
    verified: false,
    icon: "orthodontics",
    description: "Speciality guidance on alignment of teeth and bite, including clear aligner options.",
    heroDescription: "Orthodontic guidance covering the alignment of teeth and bite, including clear aligner options.",
    whatIsIt: "Orthodontic care addresses the position and alignment of teeth and jaws over a planned course of treatment.",
    whenRecommended: "Recommended after an evaluation of your bite and alignment during a consultation.",
    whatToExpect: "An assessment of your alignment, followed by a discussion of suitable options, including clear aligners.",
    process: [
      { step: "01", title: "Consultation", detail: "Assessment of alignment and bite." },
      { step: "02", title: "Treatment", detail: "A planned course of orthodontic care begins." },
      { step: "03", title: "Restoration", detail: "Progress is reviewed at regular intervals." },
    ],
    aftercare: "Following your dentist's guidance on wear-time and reviews helps keep treatment on track.",
    faqs: [{ q: "Do you offer clear aligners?", a: "Clear aligner orthodontics is one of the options that can be discussed during your consultation." }],
  },
  {
    slug: "dental-implants",
    name: "Dental Implants",
    short: "Implantology",
    category: "Surgical",
    verified: false,
    icon: "implant",
    description: "A speciality area focused on implant-based solutions for missing teeth.",
    heroDescription: "Implantology as a speciality area for patients exploring options for missing teeth.",
    whatIsIt: "Dental implants are a category of tooth-replacement options assessed on a case-by-case basis.",
    whenRecommended: "Suitability is determined through a clinical evaluation, including your general oral health.",
    whatToExpect: "A detailed evaluation and discussion of whether an implant-based approach may suit you.",
    process: [
      { step: "01", title: "Consultation", detail: "Evaluation of suitability for implant-based options." },
      { step: "02", title: "Treatment", detail: "The recommended procedure is planned and carried out." },
      { step: "03", title: "Restoration", detail: "The final restoration is placed and reviewed." },
    ],
    aftercare: "Your dentist will provide guidance specific to your procedure and healing timeline.",
    faqs: [{ q: "Am I a candidate for implants?", a: "This is best assessed through an in-person consultation and evaluation." }],
  },
  {
    slug: "paediatric-dentistry",
    name: "Paediatric Dentistry",
    short: "Children",
    category: "Children",
    verified: false,
    icon: "paediatric",
    description: "A speciality area for the dental care of children, in a comfortable setting.",
    heroDescription: "Dental care for children, delivered in a calm and comfortable setting.",
    whatIsIt: "Paediatric dentistry focuses on the dental needs of children as their teeth develop.",
    whenRecommended: "Regular check-ups from a young age support healthy dental development.",
    whatToExpect: "A gentle, age-appropriate approach designed to help children feel at ease during their visit.",
    process: [
      { step: "01", title: "Consultation", detail: "A friendly first assessment for your child." },
      { step: "02", title: "Treatment", detail: "Any recommended care is carried out at a comfortable pace." },
      { step: "03", title: "Restoration", detail: "Guidance is shared with parents on ongoing care at home." },
    ],
    aftercare: "Simple home-care habits, guided by your dentist, help support your child's oral health between visits.",
    faqs: [{ q: "At what age should my child first visit?", a: "Your dentist can advise on timing based on your child's dental development." }],
  },
  {
    slug: "teeth-capping",
    name: "Teeth Capping",
    short: "Restorative",
    category: "Restorative",
    verified: false,
    icon: "capping",
    description: "A speciality area covering dental caps for damaged or worn teeth.",
    heroDescription: "Teeth capping as an option for restoring damaged or worn teeth.",
    whatIsIt: "Teeth capping involves covering a tooth with a cap to restore its shape, size, and function.",
    whenRecommended: "This may be considered for teeth that are worn, weakened, or cosmetically affected.",
    whatToExpect: "An assessment of the tooth followed by a discussion of capping as a suitable option.",
    process: [
      { step: "01", title: "Consultation", detail: "Assessment of the affected tooth." },
      { step: "02", title: "Treatment", detail: "The tooth is prepared and the cap is fitted." },
      { step: "03", title: "Restoration", detail: "A final check ensures fit and comfort." },
    ],
    aftercare: "Good oral hygiene and regular check-ups help support the life of a dental cap.",
    faqs: [{ q: "How long does a cap typically last?", a: "This varies by material and care — your dentist can advise on your specific case." }],
  },
  {
    slug: "clear-aligners",
    name: "Clear Aligners",
    short: "Invisalign",
    category: "Orthodontic",
    verified: false,
    icon: "aligners",
    description: "A speciality area covering clear, removable aligner options for straightening teeth.",
    heroDescription: "Clear aligner orthodontics as a discreet option for straightening teeth.",
    whatIsIt: "Clear aligners are removable, transparent trays used as an alternative to traditional braces for select cases.",
    whenRecommended: "Suitability depends on your specific alignment needs, assessed during a consultation.",
    whatToExpect: "A discussion of whether clear aligners may be a suitable option for your smile goals.",
    process: [
      { step: "01", title: "Consultation", detail: "Assessment of your alignment and suitability for aligners." },
      { step: "02", title: "Treatment", detail: "A sequence of aligners is used as planned by your dentist." },
      { step: "03", title: "Restoration", detail: "Progress is reviewed at regular intervals." },
    ],
    aftercare: "Wearing aligners as recommended and attending reviews supports steady progress.",
    faqs: [{ q: "Are clear aligners noticeable?", a: "They are designed to be discreet, though your dentist can share more detail during a consultation." }],
  },
];

export const getTreatmentBySlug = (slug) => treatments.find((t) => t.slug === slug);
